// ─────────────────────────────────────────────────────────────────
// Planera jakt (J1) – den delade kärnan: deltagare, pass, lottning och markens såter.
// Används av webbens planerare (apps/web/src/views/planera/planModel.ts importerar härifrån)
// och av Q:s verktyg (QA2, mcp/huntPlanTools.ts), så att appen och Q lottar och lägger
// ut såter på samma sätt. Ren TypeScript utan importer: filen byggs både av API:t och av Vite.
// ─────────────────────────────────────────────────────────────────

export type MarkerType = 'pass' | 'hund_start' | 'samlingsplats' | 'lunch' | 'fika' | 'viltparad';

export interface PlanMarker {
  id: string;
  type: MarkerType;
  lat: number;
  lng: number;
  label: string;
  /** Mottagaren som fått passet: `m:<medlems-id>` eller `g:<e-post/telefon>`. */
  assigneeKey?: string | null;
  /** Namnet på den som fått passet (för äldre vyer som läser satPlanJson). */
  assignedName?: string;
  /** Låst pass (t.ex. jaktledare/hundförare): lottningen rör det inte. */
  locked?: boolean;
}

/** markSatId = markens såt (S1, sub_area i markgränsen) som planens såt gäller; saknas = egen såt för dagen. */
export interface PlanSat { id: string; namn: string; startTid?: string; slutTid?: string; markers: PlanMarker[]; markSatId?: string | null }

export interface Guest { key: string; name: string; email?: string; phone?: string }

export interface PlanMember { id: string; name: string; userEmail?: string | null; phone?: string | null; dogHandler?: boolean; role?: string }

/** Det i planen som deltagare, pass och lottning behöver. */
export interface PlanPeople { satar: PlanSat[]; memberIds: string[]; guests: Guest[] }

/** Fair slump för lottningen: kryptografiskt slumptal i [0, 1) (Web Crypto i både Node och webbläsaren). */
export function fairRandom(): number {
  const a = new Uint32Array(1);
  globalThis.crypto.getRandomValues(a);
  return a[0] / 2 ** 32;
}

/** Kort slumpat id (såter och pass i planen). */
export function uid(): string {
  return Math.floor(fairRandom() * 36 ** 8).toString(36).padStart(8, '0');
}

/** Fisher–Yates: varje ordning är lika sannolik när random är likformig. */
export function shuffle<T>(a: T[], random: () => number = fairRandom): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

// ─── Mottagare ────────────────────────────────────────────────────

export interface Recipient { key: string; name: string; dogHandler: boolean; isGuest: boolean; memberId?: string; email?: string; phone?: string }

export function recipientsOf(d: Pick<PlanPeople, 'memberIds' | 'guests'>, members: PlanMember[]): Recipient[] {
  const out: Recipient[] = [];
  for (const id of d.memberIds) {
    const m = members.find(x => x.id === id);
    if (m) out.push({ key: `m:${m.id}`, name: m.name, dogHandler: !!m.dogHandler, isGuest: false, memberId: m.id, email: m.userEmail || undefined, phone: m.phone || undefined });
  }
  for (const g of d.guests) out.push({ key: g.key, name: g.name, dogHandler: false, isGuest: true, email: g.email, phone: g.phone });
  return out;
}

/** Gästens nyckel i planen: `g:<e-post>` eller `g:<telefon med bara siffror och +>`. */
export function guestKey(g: { email?: string | null; phone?: string | null }): string {
  return `g:${g.email ? g.email.toLowerCase() : String(g.phone ?? '').replace(/[^\d+]/g, '')}`;
}

export interface InviteRow { memberId?: string | null; email?: string | null; phone?: string | null; name: string }

/** En sparad jakts inbjudna som planens medlemmar och gäster (medlemmar känns igen på id eller e-post). */
export function peopleFromInvites(invites: InviteRow[], members: PlanMember[]): Pick<PlanPeople, 'memberIds' | 'guests'> {
  const memberIds: string[] = [];
  const guests: Guest[] = [];
  for (const inv of invites) {
    const m = (inv.memberId && members.find(x => x.id === inv.memberId)) || (inv.email && members.find(x => x.userEmail?.toLowerCase() === inv.email!.toLowerCase()));
    if (m) { if (!memberIds.includes(m.id)) memberIds.push(m.id); continue; }
    const contact = inv.email || inv.phone;
    if (contact) guests.push({ key: guestKey(inv), name: inv.name, email: inv.email || undefined, phone: inv.phone || undefined });
  }
  return { memberIds, guests };
}

// ─── Pass ─────────────────────────────────────────────────────────

export interface Post { satIdx: number; marker: PlanMarker; label: string }

/** Alla pass och hundförarposter i planen, med visningsnamn ("Pass 3 · Såt 1"). */
export function postsOf(d: Pick<PlanPeople, 'satar'>): Post[] {
  const out: Post[] = [];
  d.satar.forEach((s, satIdx) => {
    const n = { pass: 0, hund_start: 0 };
    for (const m of s.markers) {
      if (m.type !== 'pass' && m.type !== 'hund_start') continue;
      const i = ++n[m.type];
      const name = m.label.trim() || (m.type === 'pass' ? `Pass ${i}` : `Hundförare ${i}`);
      out.push({ satIdx, marker: m, label: d.satar.length > 1 || s.namn ? `${name} · ${s.namn}` : name });
    }
  });
  return out;
}

export interface DrawResult<D extends PlanPeople = PlanPeople> { draft: D; unassigned: string[]; emptyPosts: number }

/**
 * Lottar pass bland de inbjudna: hundförare till hundförarposter, övriga till pass.
 * Låsta pass (t.ex. jaktledare/hundförare) ingår inte: de behåller sin tilldelning och den
 * som står där lottas inte. Går det inte jämnt ut blir några utan pass (eller några pass tomma)
 * — det sägs i resultatet. Går att lotta om hur många gånger som helst.
 */
export function drawPosts<D extends PlanPeople>(d: D, members: PlanMember[], random: () => number = fairRandom): DrawResult<D> {
  const posts = postsOf(d);
  const lockedKeys = new Set(posts.filter(p => p.marker.locked && p.marker.assigneeKey).map(p => p.marker.assigneeKey!));
  const everyone = recipientsOf(d, members);
  const people = everyone.filter(p => !lockedKeys.has(p.key));
  const dogs = shuffle(people.filter(p => p.dogHandler), random);
  const hunters = shuffle(people.filter(p => !p.dogHandler), random);
  const open = posts.filter(p => !p.marker.locked);
  const hundPosts = open.filter(p => p.marker.type === 'hund_start');
  const passPosts = open.filter(p => p.marker.type === 'pass');
  // Hundförare som inte får en hundförarpost lottas in bland passen.
  const extraDogs = dogs.slice(hundPosts.length);
  const shooters = shuffle([...hunters, ...extraDogs], random);
  const assign = new Map<string, Recipient | undefined>();
  hundPosts.forEach((p, i) => assign.set(p.marker.id, dogs[i]));
  passPosts.forEach((p, i) => assign.set(p.marker.id, shooters[i]));
  const satar = d.satar.map(s => ({
    ...s,
    markers: s.markers.map(m => {
      if (!assign.has(m.id)) return m;
      const r = assign.get(m.id);
      return { ...m, assigneeKey: r?.key ?? null, assignedName: r?.name };
    }),
  }));
  const assignedKeys = new Set([...lockedKeys, ...[...assign.values()].filter(Boolean).map(r => r!.key)]);
  return {
    draft: { ...d, satar },
    unassigned: everyone.filter(p => !assignedKeys.has(p.key)).map(p => p.name),
    emptyPosts: [...assign.values()].filter(r => !r).length,
  };
}

export function passLabelFor(d: Pick<PlanPeople, 'satar'>, key: string): string | null {
  const p = postsOf(d).find(x => x.marker.assigneeKey === key);
  if (!p) return null;
  return p.marker.type === 'hund_start' && !p.label.startsWith('Hundförare') ? `Hundförare · ${p.label}` : p.label;
}

/** Tar bort tilldelningar till personer som inte längre är inbjudna. */
export function pruneAssignments<D extends PlanPeople>(d: D, members: PlanMember[]): D {
  const keys = new Set(recipientsOf(d, members).map(r => r.key));
  let changed = false;
  const satar = d.satar.map(s => ({
    ...s,
    markers: s.markers.map(m => {
      if (m.assigneeKey && !keys.has(m.assigneeKey)) { changed = true; return { ...m, assigneeKey: null, assignedName: undefined }; }
      return m;
    }),
  }));
  return changed ? { ...d, satar } : d;
}

// ─── Markens såter (S1) ───────────────────────────────────────────

export interface MarkSatChoice { id: string; name: string; contains: (lat: number, lng: number) => boolean }
export interface MarkPassPin { id?: string; lat: number; lng: number; type: string; name?: string }

/**
 * Väljer eller väljer bort en av markens såter. Vald såt blir en såt i planen med markens pass
 * inom såten utlagda (namn från nålen). Bortval tar bort planens såt med dess pass.
 */
export function toggleMarkSat<D extends Pick<PlanPeople, 'satar'>>(d: D, sat: MarkSatChoice, pins: MarkPassPin[]): D {
  if (d.satar.some(s => s.markSatId === sat.id)) return { ...d, satar: d.satar.filter(s => s.markSatId !== sat.id) };
  const markers: PlanMarker[] = pins
    .filter(p => (p.type || '').toLowerCase() === 'pass' && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) && sat.contains(Number(p.lat), Number(p.lng)))
    .map(p => ({ id: uid(), type: 'pass', lat: Number(p.lat), lng: Number(p.lng), label: p.name ?? '' }));
  // En tom egen såt (t.ex. "Såt 1" utan pass) ersätts av den valda.
  const satar = d.satar.filter(s => s.markSatId || s.markers.length > 0);
  return { ...d, satar: [...satar, { id: uid(), namn: sat.name, markSatId: sat.id, markers }] };
}

/** Namnen på markens såter som planen gäller, för inbjudan. */
export function markSatNames(d: Pick<PlanPeople, 'satar'>): string[] {
  return d.satar.filter(s => s.markSatId).map(s => s.namn);
}

const MARKER_TYPES: readonly MarkerType[] = ['pass', 'hund_start', 'samlingsplats', 'lunch', 'fika', 'viltparad'];

/** Såterna i en sparad jakts sat_plan_json (redan tolkad). Äldre planer läses så gott det går. */
export function parsePlanSatar(plan: any): PlanSat[] {
  return Array.isArray(plan?.satar) ? plan.satar.map((s: any) => ({
    id: String(s.id ?? uid()), namn: String(s.namn ?? 'Såt'), startTid: s.startTid, slutTid: s.slutTid, markSatId: typeof s.markSatId === 'string' ? s.markSatId : null,
    markers: (s.markers ?? []).map((m: any) => ({ id: String(m.id ?? uid()), type: MARKER_TYPES.includes(m.type) ? m.type : 'pass', lat: Number(m.lat), lng: Number(m.lng), label: m.label ?? '', assigneeKey: m.assigneeKey ?? null, assignedName: m.assignedName, ...(m.locked ? { locked: true } : {}) })),
  })) : [];
}

/** Planens såter ur en sparad mall (mark_sat_templates): nya id:n, inga tilldelningar. */
export function templateSatar(planJson: string): PlanSat[] | null {
  const p = JSON.parse(planJson);
  return Array.isArray(p.satar) ? p.satar.map((s: any) => ({ ...s, id: uid(), markers: (s.markers ?? []).map((m: any) => ({ ...m, id: uid(), assigneeKey: null, assignedName: undefined, assignedInitials: undefined, locked: undefined })) })) : null;
}
